import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Grid, makeStyles, Typography } from '@material-ui/core';
import Modal from '../../../Shared/Modal';
import RoundedButton from '../../../Shared/RoundedButton';
import cx from 'classnames';
import { format } from 'date-fns';
import { formatNumber } from '../../utils/formatters';
import { getMetricCategory, prepareChartData } from './utils/chartUtils';
import ChartSection from './components/ChartSection';
import PatientListSection from './components/PatientListSection';

const useStyles = makeStyles(theme => ({
  modal: {
    width: '100%',
    height: '90vh',
    borderRadius: 16,
    boxShadow: '0 24px 48px rgba(0, 0, 0, 0.12)',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
  headerContent: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(3),
    flex: 1,
    justifyContent: 'flex-end',
  },
  headerMetricSummary: {
    display: 'flex',
    gap: theme.spacing(2),
    padding: theme.spacing(1, 2),
    background: 'rgba(59, 130, 246, 0.04)',
    borderRadius: theme.shape.borderRadius,
    alignItems: 'center',
    whiteSpace: 'nowrap',
    flexShrink: 0,
  },
  headerSummaryItem: {
    textAlign: 'center',
    minWidth: 60,
  },
  headerSummaryValue: {
    fontSize: 18,
    fontWeight: 600,
    lineHeight: 1.2,
  },
  headerSummaryLabel: {
    fontSize: 10,
    color: theme.colors.blue2,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    marginTop: 2,
  },
  headerDivider: {
    width: 1,
    height: 32,
    background: 'rgba(0, 0, 0, 0.08)',
  },
  modalComparison: {
    maxWidth: '95vw',
    width: '95vw',
  },
  modalSingle: {
    maxWidth: '90vw',
    width: '90vw',
  },
  scrollableContent: {
    flex: 1,
    padding: theme.spacing(0, 4),
    background: 'linear-gradient(135deg, #fafafa 0%, #ffffff 100%)',
    display: 'flex',
    flexDirection: 'column',
    '@media (min-height: 700px)': {
      overflow: 'hidden',
      height: '100%',
    },
    '@media (max-height: 699px)': {
      overflow: 'auto',
    },
  },
  contentContainer: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    minHeight: 0,
  },
  gridContainer: {
    flex: 1,
    minHeight: 0,
    display: 'flex',
    '@media (min-height: 700px)': {
      height: 'calc(100% - 100px)',
    },
  },
  actionsContainer: {
    padding: theme.spacing(3, 6),
    background: '#ffffff',
    borderTop: '1px solid rgba(0, 0, 0, 0.12)',
    borderRadius: '0 0 16px 16px',
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
    gap: theme.spacing(2),
    flexShrink: 0,
    boxShadow: '0 -4px 16px rgba(0, 0, 0, 0.08)',
    width: '100%',
  },
  patientListGridItem: {
    display: 'flex',
    flexDirection: 'column',
    minHeight: 0,
    maxHeight: '100%',
    overflow: 'hidden',
    [theme.breakpoints.down('sm')]: {
      // maxHeight: 'calc(100vh - 500px)', // Less height on tablets due to metric info above
    },
  },
  actionButton: {
    textTransform: 'uppercase',
    borderRadius: 10,
    padding: theme.spacing(1.5, 3),
    letterSpacing: 1.5,
    fontWeight: 600,
    fontSize: 14,
    minWidth: 100,
    height: 40,
  },
  filterInfo: {
    fontSize: 13,
    color: theme.palette.text.secondary,
    fontStyle: 'italic',
    '& strong': {
      fontWeight: 600,
      color: theme.palette.text.primary,
      fontStyle: 'normal',
    },
  },
}));

const PatientDrilldownModal = ({ open, onClose, metric, totalPatients, isComparisonMode = false }) => {
  const classes = useStyles();
  const {
    en: { btn, populationHealth },
  } = useSelector(state => state.i18n);
  const isDistributionSponsor = useSelector((state) => state.user.isDistributionSponsor);

  // Get filters from Redux store
  const {
    dateRange,
    selectedAccount,
    selectedInstrument,
    assessmentInstruments
  } = useSelector((state) => state.populationHealth);

  // Get metric category and prepare chart data
  const { category, chartData, baselineCount, followupCount } = useMemo(() => {
    if (!metric) return { category: null, chartData: [], baselineCount: 0, followupCount: 0 };

    const cat = getMetricCategory(metric.label);
    const chart = prepareChartData(metric, isComparisonMode);

    return { category: cat, ...chart };
  }, [metric, isComparisonMode]);

  // Prepare header content with MetricSummary and filter description
  const headerContent = useMemo(() => {
    const filterParts = [];

    // Date range
    if (dateRange?.startDate && dateRange?.endDate) {
      const startStr = format(new Date(dateRange.startDate), 'MMM d, yyyy');
      const endStr = format(new Date(dateRange.endDate), 'MMM d, yyyy');
      const dateStr = `${startStr} - ${endStr}`;
      filterParts.push(<><strong>{dateStr}</strong></>);
    }

    // Account (only for DS)
    if (isDistributionSponsor && selectedAccount) {
      const accountName = selectedAccount.businessName || selectedAccount.name || selectedAccount.accountName;
      if (accountName) {
        filterParts.push(<><strong>{accountName}</strong></>);
      }
    }

    // Assessment
    if (selectedInstrument && selectedInstrument !== 'all') {
      const assessment = assessmentInstruments?.find(a => a.id === selectedInstrument);
      const assessmentName = assessment?.name || selectedInstrument;
      filterParts.push(<><strong>{assessmentName}</strong></>);
    }

    const filterDescription = filterParts.length > 0 ? (
      <Typography className={classes.filterInfo}>
        {'Showing data for: '}
        {filterParts.map((part, index) => (
          <>
          <span key={`sep-${index}`} style={{padding: '0 8px'}}>
            {index > 0 && ' • '}
          </span>
          <span key={`part-${index}`} style={{ whiteSpace: 'nowrap' }}>
            {part}
          </span>
          </>
        ))}
      </Typography>
    ) : null;

    // Return combined header content
    return (
      <div className={classes.headerContent}>
        {/* Compact Metric Summary */}
        <div className={classes.headerMetricSummary}>
          {isComparisonMode ? (
            <>
              <div className={classes.headerSummaryItem}>
                <div className={classes.headerSummaryValue} style={{ color: category?.color, opacity: 0.6 }}>
                  {formatNumber(baselineCount)}
                </div>
                <div className={classes.headerSummaryLabel}>Baseline</div>
              </div>
              <div className={classes.headerDivider} />
              <div className={classes.headerSummaryItem}>
                <div className={classes.headerSummaryValue} style={{ color: category?.color }}>
                  {formatNumber(followupCount)}
                </div>
                <div className={classes.headerSummaryLabel}>Follow-up</div>
              </div>
              <div className={classes.headerDivider} />
              <div className={classes.headerSummaryItem}>
                <div className={classes.headerSummaryValue} style={{
                  color: followupCount > baselineCount ? '#DC2626' : followupCount < baselineCount ? '#16A34A' : '#6B7280'
                }}>
                  {followupCount > baselineCount ? '+' : ''}{formatNumber(followupCount - baselineCount)}
                </div>
                <div className={classes.headerSummaryLabel}>Change</div>
              </div>
            </>
          ) : (
            <>
              <div className={classes.headerSummaryItem}>
                <div className={classes.headerSummaryValue} style={{ color: category?.color }}>
                  {formatNumber(metric?.count || 0)}
                </div>
                <div className={classes.headerSummaryLabel}>Patients</div>
              </div>
              <div className={classes.headerDivider} />
              <div className={classes.headerSummaryItem}>
                <div className={classes.headerSummaryValue} style={{ color: category?.color }}>
                  {metric?.percent}%
                </div>
                <div className={classes.headerSummaryLabel}>Of Total</div>
              </div>
            </>
          )}
          <div className={classes.headerDivider} />
          <div className={classes.headerSummaryItem}>
            <div className={classes.headerSummaryValue} style={{ color: category?.color }}>
              {category?.name}
            </div>
            <div className={classes.headerSummaryLabel}>Risk Level</div>
          </div>
        </div>
        {/* Filter Description */}
        {filterDescription}
      </div>
    );
  }, [dateRange, selectedAccount, selectedInstrument, assessmentInstruments, isDistributionSponsor,
      classes, category, baselineCount, followupCount, isComparisonMode, metric]);

  if (!metric) return null;

  const modalClass = cx(
    classes.modal,
    isComparisonMode ? classes.modalComparison : classes.modalSingle
  );

  return (
    <Modal
      open={open}
      handleClose={onClose}
      classes={{ paper: modalClass }}
      title={metric.label}
      titleChildren={headerContent}
    >
      <div className={classes.scrollableContent}>
        <div className={classes.contentContainer}>
          <Grid container spacing={3} className={classes.gridContainer}>
            {/* Chart */}
            <Grid item xs={12} md={4}>
              <ChartSection
                chartData={chartData}
                category={category}
                isComparisonMode={isComparisonMode}
                metric={metric}
                baselineCount={baselineCount}
                followupCount={followupCount}
                totalPatients={totalPatients}
              />
            </Grid>

            {/* Patient Lists */}
            {open && <Grid item xs={12} md={8} className={classes.patientListGridItem}>
              <PatientListSection
                metric={metric}
              />
            </Grid>}
          </Grid>
        </div>
      </div>

      <div className={classes.actionsContainer}>
        <RoundedButton
          variant="outlined"
          color="primary"
          onClick={onClose}
          className={classes.actionButton}
        >
          {btn?.close || 'Close'}
        </RoundedButton>
      </div>
    </Modal>
  );
};

export default PatientDrilldownModal;
